export interface OhlcPriceBar {
  date: string | Date;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface AtrExtension {
  atrExtension: number;
  currentClose: number;
  sma50: number;
  atr: number;
}

function simpleMovingAverage(values: readonly number[]): number {
  return values.reduce((total, value) => total + value, 0) / values.length;
}

export function getATRExtension(
  priceData: readonly OhlcPriceBar[],
  period = 14,
  smaPeriod = 50,
): AtrExtension {
  const minimumBars = smaPeriod + period;
  if (priceData.length < minimumBars) {
    throw new Error(
      `ATR extension requires at least ${minimumBars} price bars; received ${priceData.length}.`,
    );
  }

  const trueRanges = priceData.map((bar, index) => {
    const previousBar = index > 0 ? priceData[index - 1] : undefined;
    if (!previousBar) return bar.high - bar.low;
    return Math.max(
      bar.high - bar.low,
      Math.abs(bar.high - previousBar.close),
      Math.abs(bar.low - previousBar.close),
    );
  });
  const atr = simpleMovingAverage(trueRanges.slice(-period));
  const closingPrices = priceData.slice(-smaPeriod).map((bar) => bar.close);
  const sma50 = simpleMovingAverage(closingPrices);
  const currentClose = priceData[priceData.length - 1]?.close;

  if (currentClose === undefined) {
    throw new Error('ATR extension requires a current closing price.');
  }
  // extension = (price - 50SMA) / ATR(14), used to flag statistically stretched stocks for display/context only, not as a trade signal.
  const atrExtension = Number(((currentClose - sma50) / atr).toFixed(2));

  return { atrExtension, currentClose, sma50, atr };
}
